import { Looker40SDK } from '@looker/sdk';

import { containsValues, query } from '@/api/looker/utils/query';
import { AlertStatus } from '@/entities/alert-status';
import { PCSAlgorithm } from '@/pcs/domain/entities/pcs-algorithm';
import type { PCSOverview } from '@/pcs/domain/entities/pcs-overview';
import { AlertStatusFromValue } from '@/pcs/domain/entities/score';
import { IPCSRepository } from '@/pcs/domain/repositories/pcs.repository';

import {
  type PCSPowerAvailabilityRow,
  transformPowerAvailabilityRows,
} from './mappers/power-availability.pcs.repository.mapper';
import {
  type PCSPowerCapabilityRow,
  transformPowerCapabilityRows,
} from './mappers/power-capability.pcs.repository.mapper';
import {
  type PCSPowerEfficiencyCHARow,
  type PCSPowerEfficiencyDCHRow,
  type PCSPowerEfficiencyRow,
  transformPowerEfficiencyCHARows,
  transformPowerEfficiencyDCHRows,
} from './mappers/power-efficiency.pcs.repository.mapper';

// map PCSAlgorithm to respective Looker column

const tablesFromAlgorithm: Record<PCSAlgorithm, string> = {
  [PCSAlgorithm.PCS_EFFICIENCY]: 'mantle_pcs_status.pcs_efficiency_score_max',
  [PCSAlgorithm.POWER_AVAILABILITY]:
    'mantle_pcs_status.power_availability_score_max',
  [PCSAlgorithm.POWER_CAPABILITY]:
    'mantle_pcs_status.power_capability_score_max',
};

type StatusTableResult = {
  id: string;
  fullFrontendId: string;
  type: string;
  inverterType: string;
  powerMaxCha: number;
  powerMaxDch: number;
  score: number;
  algorithm_uid: PCSAlgorithm;
  status: AlertStatus;
};

type StatusTableResultData = {
  statusTableResult: StatusTableResult[];
};

type AggregatedResult = {
  id: string;
  power_availability_avg: number;
  power_capability_cha_avg: number;
  power_capability_dch_avg: number;
  pcs_efficiency_cha_avg: number;
  pcs_efficiency_dch_avg: number;
};

type AggregatedResultData = {
  aggregatedResult: AggregatedResult[];
};

function normalizeValue<T>(value: T | null): T | undefined {
  return value === null ? undefined : value;
}

function mergeStatusAggregationResults(
  statusTableResult: StatusTableResultData, // statisticsData: SitesStatisticsData,
  aggregatedResult: AggregatedResultData, // alertsData: SitesAlertsData,
): IPCSRepository.GetAllOutput {
  // create map for siteStatistics
  const statusTableResultMap = new Map<string, StatusTableResult>();
  statusTableResult.statusTableResult.forEach((stat) => {
    statusTableResultMap.set(stat.id, stat);
  });

  // create map for aggregated results
  const aggregatedResultMap = new Map<string, AggregatedResult>();
  aggregatedResult.aggregatedResult.forEach((stat) => {
    aggregatedResultMap.set(stat.id, stat);
  });

  // combine to siteStatus
  const pcsOverview: PCSOverview[] = [];

  statusTableResultMap.forEach((stat) => {
    const aggResult = aggregatedResultMap.get(stat.id);

    const powerAvailabilityAvg = normalizeValue(
      aggResult?.power_availability_avg,
    );
    let powerCapabilityDCHAvg = normalizeValue(
      aggResult?.power_capability_dch_avg,
    );
    let powerCapabilityCHAAvg = normalizeValue(
      aggResult?.power_capability_cha_avg,
    );
    let pcsEfficiencyDCHAvg = normalizeValue(aggResult?.pcs_efficiency_dch_avg);
    let pcsEfficiencyCHAAvg = normalizeValue(aggResult?.pcs_efficiency_cha_avg);

    // if power_availability_avg is 0, set the other averages to undefined
    if (powerAvailabilityAvg === 0) {
      powerCapabilityDCHAvg = undefined;
      powerCapabilityCHAAvg = undefined;
      pcsEfficiencyDCHAvg = undefined;
      pcsEfficiencyCHAAvg = undefined;
    }

    const siteStatus: PCSOverview = {
      id: stat.id,
      fullFrontendId: stat.fullFrontendId,
      type: stat.type,
      inverterType: stat.inverterType,
      powerMaxCha: stat.powerMaxCha,
      powerMaxDch: stat.powerMaxDch,
      powerAvailabilityAvgLast7d: powerAvailabilityAvg,
      powerCapabilityDCHAvgLast7d: powerCapabilityDCHAvg,
      powerCapabilityCHAAvgLast7d: powerCapabilityCHAAvg,
      pcsEfficiencyDCHAvgLast7d: pcsEfficiencyDCHAvg,
      pcsEfficiencyCHAAvgLast7d: pcsEfficiencyCHAAvg,
      score: stat.score,
      algorithmUID: stat.algorithm_uid,
      status: stat.status,
    };

    pcsOverview.push(siteStatus);
  });

  return pcsOverview;
}

export class LookerPCSRepository implements IPCSRepository {
  constructor(
    private coreSDK: Looker40SDK,
    private lookerModel: string,
  ) {}

  get key() {
    return this.lookerModel;
  }

  async getAll(
    input: IPCSRepository.GetAllInput,
  ): Promise<IPCSRepository.GetAllOutput> {
    const [statusTableResult, aggregatedResult] = await Promise.all([
      // 1. Get static results from _status table
      query(this.coreSDK)
        .from<{
          'mantle_pcs_status.accure_id': string;
          'frontend_id_mapping.full_frontend_id': string;
          'mantle_pcs_status.device_type': string;
          'mantle_pcs_status.power_max_cha': number;
          'mantle_pcs_status.power_max_dch': number;
          'mantle_pcs_status.score': number;
          'mantle_pcs_status.algorithm_uid': PCSAlgorithm;
          'mantle_pcs_status.status': AlertStatus;
        }>({
          model: this.lookerModel,
          view: 'mantle_pcs',
          fields: [
            'mantle_pcs_status.accure_id',
            'frontend_id_mapping.full_frontend_id',
            'mantle_pcs_status.device_type',
            'mantle_pcs_status.power_max_cha',
            'mantle_pcs_status.power_max_dch',
            'mantle_pcs_status.score',
            'mantle_pcs_status.algorithm_uid',
            'mantle_pcs_status.status',
          ],
          filters: {
            'frontend_id_mapping.frontend_group_id': input.assetFullFrontendId
              ? containsValues([input.assetFullFrontendId])
              : '',
            'frontend_id_mapping.full_frontend_id': input.filters?.pcsId
              ? containsValues([input.filters?.pcsId])
              : '',
          },
          sorts: ['frontend_id_mapping.full_frontend_id asc'],
        })
        .toMany<StatusTableResult>((result) => {
          const rawString = result['mantle_pcs_status.device_type'] || '';
          const pcsTypeMatch = rawString.match(/(?<=pcs_)([^]*?)(?=_inv|$)/);
          const invTypeMatch = rawString.match(/(?<=inv_)(.*)$/);
          return {
            id: result['mantle_pcs_status.accure_id'],
            fullFrontendId: result['frontend_id_mapping.full_frontend_id'],
            type: pcsTypeMatch?.[1] || '',
            inverterType: invTypeMatch?.[1] || '',
            powerMaxCha: result['mantle_pcs_status.power_max_cha'],
            powerMaxDch: result['mantle_pcs_status.power_max_dch'],
            score: result['mantle_pcs_status.score'],
            algorithm_uid: result['mantle_pcs_status.algorithm_uid'],
            status:
              AlertStatusFromValue[result['mantle_pcs_status.score']] ??
              AlertStatus.OK,
          };
        }),

      // 2. Get aggregated results from _timeseries table
      query(this.coreSDK)
        .from<{
          'mantle_pcs_status.accure_id': string;
          'mantle_pcs_timeseries.power_availability_avg': number;
          'mantle_pcs_timeseries.power_capability_cha_avg_meas': number;
          'mantle_pcs_timeseries.power_capability_dch_avg_meas': number;
          'mantle_pcs_timeseries.pcs_efficiency_cha_avg_meas': number;
          'mantle_pcs_timeseries.pcs_efficiency_dch_avg_meas': number;
        }>({
          model: this.lookerModel,
          view: 'mantle_pcs',
          fields: [
            'mantle_pcs_status.accure_id',
            'mantle_pcs_timeseries.power_availability_avg',
            'mantle_pcs_timeseries.power_capability_cha_avg_meas',
            'mantle_pcs_timeseries.power_capability_dch_avg_meas',
            'mantle_pcs_timeseries.pcs_efficiency_cha_avg_meas',
            'mantle_pcs_timeseries.pcs_efficiency_dch_avg_meas',
          ],
          filters: {
            'frontend_id_mapping.frontend_group_id': input.assetFullFrontendId
              ? containsValues([input.assetFullFrontendId])
              : '',
            'frontend_id_mapping.full_frontend_id': input.filters?.pcsId
              ? containsValues([input.filters?.pcsId])
              : '',
            'mantle_pcs_timeseries.window_end_date': '7 days', // last 7 days for aggregated stats
          },
        })
        .toMany<AggregatedResult>((result) => ({
          id: result['mantle_pcs_status.accure_id'],
          power_availability_avg:
            result['mantle_pcs_timeseries.power_availability_avg'],
          power_capability_cha_avg:
            result['mantle_pcs_timeseries.power_capability_cha_avg_meas'],
          power_capability_dch_avg:
            result['mantle_pcs_timeseries.power_capability_dch_avg_meas'],
          pcs_efficiency_cha_avg:
            result['mantle_pcs_timeseries.pcs_efficiency_cha_avg_meas'],
          pcs_efficiency_dch_avg:
            result['mantle_pcs_timeseries.pcs_efficiency_dch_avg_meas'],
        })),
    ]);

    return mergeStatusAggregationResults(
      { statusTableResult },
      { aggregatedResult },
    );
  }

  async getPowerAvailability(
    input: IPCSRepository.GetPowerAvailabilityInput,
  ): Promise<IPCSRepository.GetPowerAvailabilityOutput> {
    const rows = await query(this.coreSDK)
      .from<PCSPowerAvailabilityRow>({
        model: this.lookerModel,
        view: 'mantle_pcs',
        fields: [
          'mantle_pcs_status.accure_id',
          'frontend_id_mapping.full_frontend_id',
          'mantle_pcs_status.power_availability_score',
          'mantle_pcs_timeseries.window_end_date',
          'mantle_pcs_timeseries.power_availability_gap',
          'mantle_pcs_timeseries.power_availability_invalid',
        ],
        filters: {
          'mantle_pcs_timeseries.window_end_date': '30 days',
          'frontend_id_mapping.frontend_group_id': input.assetFullFrontendId
            ? containsValues([input.assetFullFrontendId])
            : '',
          'frontend_id_mapping.full_frontend_id': input.filters?.pcsId
            ? containsValues([input.filters?.pcsId])
            : '',
        },
        sorts: ['frontend_id_mapping.full_frontend_id asc'],
      })
      .toMany((row) => row);

    const metric = transformPowerAvailabilityRows(rows);

    return metric;
  }

  async getPowerCapability(
    input: IPCSRepository.GetPowerCapabilityInput,
  ): Promise<IPCSRepository.GetPowerCapabilityOutput> {
    const rows = await query(this.coreSDK)
      .from<PCSPowerCapabilityRow>({
        model: this.lookerModel,
        view: 'mantle_pcs',
        fields: [
          'mantle_pcs_status.accure_id',
          'frontend_id_mapping.full_frontend_id',
          'mantle_pcs_status.power_capability_score',
          'mantle_pcs_status.power_max_dch',
          'mantle_pcs_status.power_max_cha',
          'mantle_pcs_timeseries.window_end_date',
          'mantle_pcs_timeseries.power_capability_dch_max',
          'mantle_pcs_timeseries.power_capability_dch_avg',
          'mantle_pcs_timeseries.power_capability_dch_min',
          'mantle_pcs_timeseries.power_capability_cha_max',
          'mantle_pcs_timeseries.power_capability_cha_avg',
          'mantle_pcs_timeseries.power_capability_cha_min',
        ],
        filters: {
          'mantle_pcs_timeseries.window_end_date': '30 days',
          'frontend_id_mapping.frontend_group_id': input.assetFullFrontendId
            ? containsValues([input.assetFullFrontendId])
            : '',
          'frontend_id_mapping.full_frontend_id': input.filters?.pcsId
            ? containsValues([input.filters?.pcsId])
            : '',
        },
        sorts: [
          'frontend_id_mapping.full_frontend_id asc',
          'mantle_pcs_timeseries.window_end_date',
        ],
      })
      .toMany((row) => row);

    const metric = transformPowerCapabilityRows(rows);

    return metric;
  }

  async getPowerEfficiency(
    input: IPCSRepository.GetPowerEfficiencyInput,
  ): Promise<IPCSRepository.GetPowerEfficiencyOutput> {
    const [rowsDCH, rowsCHA] = await Promise.all([
      query(this.coreSDK)
        .from<PCSPowerEfficiencyDCHRow>({
          model: this.lookerModel,
          view: 'mantle_pcs',
          fields: [
            'mantle_pcs_status.accure_id',
            'frontend_id_mapping.full_frontend_id',
            'mantle_pcs_status.pcs_efficiency_score',
            'mantle_pcs_timeseries.window_end_date',
            'mantle_pcs_timeseries.pcs_efficiency_dch_avg',
            'mantle_pcs_timeseries.pcs_efficiency_dch_min',
            'mantle_pcs_timeseries.pcs_efficiency_dch_max',
          ],
          filters: {
            'mantle_pcs_timeseries.window_end_date': '30 days',
            'frontend_id_mapping.frontend_group_id': input.assetFullFrontendId
              ? containsValues([input.assetFullFrontendId])
              : '',
            'frontend_id_mapping.full_frontend_id': input.filters?.pcsId
              ? containsValues([input.filters?.pcsId])
              : '',
          },
          sorts: [
            'frontend_id_mapping.full_frontend_id asc',
            'mantle_pcs_timeseries.window_end_date',
          ],
        })
        .toMany<PCSPowerEfficiencyRow>((result) => ({
          id: result['mantle_pcs_status.accure_id'],
          accure_id: result['mantle_pcs_status.accure_id'],
          full_frontend_id: result['frontend_id_mapping.full_frontend_id'],
          pcs_efficiency_score:
            result['mantle_pcs_status.pcs_efficiency_score'],
          window_end_date: result['mantle_pcs_timeseries.window_end_date'],
          pcs_efficiency_avg:
            result['mantle_pcs_timeseries.pcs_efficiency_dch_avg'],
          pcs_efficiency_min:
            result['mantle_pcs_timeseries.pcs_efficiency_dch_min'],
          pcs_efficiency_max:
            result['mantle_pcs_timeseries.pcs_efficiency_dch_max'],
        })),
      query(this.coreSDK)
        .from<PCSPowerEfficiencyCHARow>({
          model: this.lookerModel,
          view: 'mantle_pcs',
          fields: [
            'mantle_pcs_status.accure_id',
            'frontend_id_mapping.full_frontend_id',
            'mantle_pcs_status.pcs_efficiency_score',
            'mantle_pcs_timeseries.window_end_date',
            'mantle_pcs_timeseries.pcs_efficiency_cha_avg',
            'mantle_pcs_timeseries.pcs_efficiency_cha_min',
            'mantle_pcs_timeseries.pcs_efficiency_cha_max',
          ],
          filters: {
            'mantle_pcs_timeseries.window_end_date': '30 days',
            'frontend_id_mapping.frontend_group_id': input.assetFullFrontendId
              ? containsValues([input.assetFullFrontendId])
              : '',
            'frontend_id_mapping.full_frontend_id': input.filters?.pcsId
              ? containsValues([input.filters?.pcsId])
              : '',
          },
          sorts: [
            'frontend_id_mapping.full_frontend_id asc',
            'mantle_pcs_timeseries.window_end_date',
          ],
        })
        .toMany<PCSPowerEfficiencyRow>((result) => ({
          id: result['mantle_pcs_status.accure_id'],
          accure_id: result['mantle_pcs_status.accure_id'],
          full_frontend_id: result['frontend_id_mapping.full_frontend_id'],
          pcs_efficiency_score:
            result['mantle_pcs_status.pcs_efficiency_score'],
          window_end_date: result['mantle_pcs_timeseries.window_end_date'],
          pcs_efficiency_avg:
            result['mantle_pcs_timeseries.pcs_efficiency_cha_avg'],
          pcs_efficiency_min:
            result['mantle_pcs_timeseries.pcs_efficiency_cha_min'],
          pcs_efficiency_max:
            result['mantle_pcs_timeseries.pcs_efficiency_cha_max'],
        })),
    ]);

    const powerEfficiencyDCH = transformPowerEfficiencyDCHRows(rowsDCH);
    const powerEfficiencyCHA = transformPowerEfficiencyCHARows(rowsCHA);

    return { powerEfficiencyDCH, powerEfficiencyCHA };
  }

  async getAlgorithmStatus(
    input: IPCSRepository.GetAlgorithmStatusInput,
  ): Promise<IPCSRepository.GetAlgorithmStatusOutput> {
    const maxScore = await query(this.coreSDK)
      .from({
        model: this.lookerModel,
        view: 'mantle_pcs',
        fields: [tablesFromAlgorithm[input.algorithm]],
        filters: {
          'frontend_id_mapping.frontend_group_id': input.assetFullFrontendId
            ? containsValues([input.assetFullFrontendId])
            : '',
          'frontend_id_mapping.full_frontend_pcs_id': input.filters?.pcsId
            ? containsValues([input.filters?.pcsId])
            : '',
        },
      })
      .toFirst<number>(
        (result) => result[tablesFromAlgorithm[input.algorithm]],
      );

    return AlertStatusFromValue[maxScore] ?? AlertStatus.OK;
  }
}
